import React, { useState, useEffect, useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { getMeasurements, getDailyMeasurements, getIrrigationEvents } from '../../services/sensorGraphs/sensorGraphsService';
import LoadingComponent from '../loading/LoadingComponent';
import IrrigationEventsModal from './IrrigationEventsModal';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import CsvDownloadButton from 'react-json-to-csv';
import {
    operationForChangeVbValue,
    operationForChangeCeValue,
    operationForChangePhValue,
    operationForAdaptDailyValue,
} from '../utils/AdjustData';
import { graphColors } from '../utils/GraphUtils';
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css';
import 'react-day-picker/dist/style.css';
import 'bootstrap/dist/css/bootstrap.css';
import '../../styles/graphs.css';
import ModalUpdateThreshold from './ModalUpdateThreshold';
import ModalSelectDateMeasure from './ModalSelectDateMeasure';
import {
    SINGLE_VALUE_DAILY_VARS_CONTS,
    VARIABLES_WITH_ADJUSTED_CE,
    VARIABLES_WITH_ADJUSTED_PH
} from '../utils/Constants';

const SINGLE_VALUE_DAILY_VARS = SINGLE_VALUE_DAILY_VARS_CONTS;
const SINGLE_VALUE_DAILY_VARS_CONTS_WITHOUT_EVENTS = SINGLE_VALUE_DAILY_VARS_CONTS;


const VariableGraph = ({ values, sensorName }) => {
    const navigate = useNavigate();
    const graphData = values.graphData;


    // Determine the actual view mode of the data currently held in graphData
    // Default to 'realtime' if undefined (e.g. initial load or old data)
    const currentViewMode = graphData.viewMode || 'realtime';

    // States
    const [sensorId] = useState(graphData.sensorId);
    const [varId] = useState(graphData.varId);
    const [varName] = useState(graphData.varName);
    const [data, setData] = useState([]);
    const [units] = useState(graphData.units);
    const [color] = useState(graphColors[graphData.data[0].id] || ['#1f77b4']);
    const [range, setRange] = useState({ from: undefined, to: undefined });
    const [oldRange, setOldRange] = useState({ from: undefined, to: undefined });
    const [showModalThreshold, setShowModalThreshold] = useState(false);
    // Umbral guardado: se actualiza al guardar para no depender de recargar los datos del backend
    const [savedThreshold, setSavedThreshold] = useState({
        max: graphData.actualMaxThreshold,
        min: graphData.actualMinThreshold
    });

    useEffect(() => {
        setSavedThreshold({ max: graphData.actualMaxThreshold, min: graphData.actualMinThreshold });
    }, [graphData.actualMaxThreshold, graphData.actualMinThreshold]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [loaded, setLoaded] = useState(true);
    const [visibleSeries, setVisibleSeries] = useState(['Max', 'Prom', 'Min']);
    const [showEventsModal, setShowEventsModal] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [selectedDateLabel, setSelectedDateLabel] = useState("");
    const [irrigationSummary, setIrrigationSummary] = useState(null);

    // Effect to handle validation of varId and update data
    useEffect(() => {
        if (!varId) {
            navigate('/error');
        }

        // Process data based on the view mode of the DATA itself
        if (currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId)) {
            const rawData = graphData.data[0].data;

            if (rawData.length > 0 && rawData[0].avg !== undefined) {
                setData([
                    { id: 'Max', color: '#F59E0B', data: rawData.map(d => ({ x: d.x, y: d.max, occurredAt: d.maxOccurredAt })) },
                    { id: 'Prom', color: '#3B82F6', data: rawData.map(d => ({ x: d.x, y: d.avg })) },
                    { id: 'Min', color: '#15803D', data: rawData.map(d => ({ x: d.x, y: d.min, occurredAt: d.minOccurredAt })) }
                ]);
            } else {
                setData(graphData.data);
            }

        } else if (currentViewMode === 'daily' && varId === 'RHS') {
            const rawData = graphData.data[0].data;
            setData([
                {
                    id: 'RHS',
                    data: rawData.map(d => ({
                        x: d.x,
                        y: d.avg, // Pulse count
                        duration: d.max, // Sum of durations
                        firstPulse: d.minOccurredAt,
                        lastPulse: d.maxOccurredAt
                    }))
                }
            ]);
        } else {
            setData(graphData.data);
        }

    }, [varId, sensorId, navigate, graphData, currentViewMode]);

    // Loading properties
    const loadingProps = {
        message: '',
        type: 'success',
    };

    // Format date to a readable format
    const formatDate = (date) => {
        const pad = (num) => (num < 10 ? '0' + num : num);
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // Convert UTC time to local time format
    const convertToLocalTime = (data) => {
        return data.map(item => {
            const localDate = new Date(item.x);
            return {
                ...item,
                x: formatDate(localDate),
            };
        });
    };

    // Prepare CSV data based on view mode
    const prepareCsvData = () => {
        if (data.length === 0) return [];

        // For daily mode with multiple series (Max, Prom, Min)
        if (currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId) && data.length === 3) {
            // Combine all three series into one array with all columns
            const maxData = data.find(d => d.id === 'Max')?.data || [];
            const promData = data.find(d => d.id === 'Prom')?.data || [];
            const minData = data.find(d => d.id === 'Min')?.data || [];

            return maxData.map((item, index) => {
                const localDate = new Date(item.x);
                const maxHour = new Date(maxData[index].occurredAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                })

                const minHour = new Date(minData[index].occurredAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                })

                return {
                    Fecha: localDate.toISOString().split('T')[0], // Solo fecha para daily
                    Promedio: promData[index]?.y || '',
                    Max: maxData[index]?.y || '',
                    HoraMax: maxHour,
                    Min: minData[index]?.y || '',
                    HoraMin: minHour
                };
            });
        }

        if (currentViewMode === 'daily' && varId === 'RHS') {
            return data[0].data.map((item, index) => {
                const localDate = new Date(item.x);
                const firstPulse = item.firstPulse ? new Date(item.firstPulse).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }) : ''

                const lastPulse = item.lastPulse ? new Date(item.lastPulse).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }) : ''

                return {
                    Fecha: localDate.toISOString().split('T')[0], // Solo fecha para daily
                    Conteo: item?.y || '0',
                    Duracion: item?.duration || '',
                    PrimerPulso: firstPulse,
                    UltimoPulso: lastPulse
                };
            });
        }

        // For daily EVT, GDD - only date without time
        if (currentViewMode === 'daily' && SINGLE_VALUE_DAILY_VARS_CONTS_WITHOUT_EVENTS.includes(varId)) {
            return data[0].data.map(item => {
                const localDate = new Date(item.x);
                return {
                    Fecha: localDate.toISOString().split('T')[0], // Solo fecha para daily value unico
                    [varName]: item.y
                };
            });
        }

        // For realtime (with full date and time)
        return convertToLocalTime(data[0].data).map(item => ({
            [varName]: item.y,
            Hora: item.x
        }));
    };

    // Get CSV headers based on view mode
    const getCsvHeaders = () => {
        if (currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId) && data.length === 3) {
            return ['Fecha', 'Promedio', 'Max', 'HoraMax', 'Min', 'HoraMin'];
        }
        if (currentViewMode === 'daily' && varId === 'RHS') {
            return ['Fecha', 'Conteo', 'Duracion', 'PrimerPulso', 'UltimoPulso'];
        }
        if (currentViewMode === 'daily' && SINGLE_VALUE_DAILY_VARS.includes(varId)) {
            return ['Fecha', varName];
        }
        return [varName, 'Hora'];
    };

    // Unified function to fetch measurements based on view mode
    const requestMeasures = async (startDate, endDate) => {
        setLoaded(false);
        try {
            let res;
            if (currentViewMode === 'daily') {
                res = await getDailyMeasurements(sensorId, varId, startDate, endDate);
            } else {
                res = await getMeasurements(sensorId, varId, startDate, endDate);
            }

            if (res.status === 200) {
                const measures = res.data.measures || [];
                if (measures.length > 0) {
                    // Apply common transformations
                    if (varId === 'VB') operationForChangeVbValue(measures);
                    if (VARIABLES_WITH_ADJUSTED_CE.includes(varId)) operationForChangeCeValue(measures, sensorId);
                    if (VARIABLES_WITH_ADJUSTED_PH.includes(varId)) operationForChangePhValue(measures, sensorId);
                    if (SINGLE_VALUE_DAILY_VARS.includes(varId)) operationForAdaptDailyValue(measures);

                    // Process data based on view mode
                    if (currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId)) {
                        if (measures.length > 0 && measures[0].avg !== undefined) {
                            setData([
                                { id: 'Max', color: '#F59E0B', data: measures.map(d => ({ x: d.x, y: d.max, occurredAt: d.maxOccurredAt })) },
                                { id: 'Prom', color: '#3B82F6', data: measures.map(d => ({ x: d.x, y: d.avg })) },
                                { id: 'Min', color: '#15803D', data: measures.map(d => ({ x: d.x, y: d.min, occurredAt: d.minOccurredAt })) }
                            ]);
                        } else {
                            setData([{ id: varId, data: measures }]);
                        }
                    } else if (currentViewMode === 'daily' && varId === 'RHS') {
                        setData([
                            {
                                id: 'RHS',
                                data: measures.map(d => ({
                                    x: d.x,
                                    y: d.avg, // Pulse count
                                    duration: d.max, // Sum of durations
                                    firstPulse: d.minOccurredAt,
                                    lastPulse: d.maxOccurredAt
                                }))
                            }
                        ]);
                    }
                    else {
                        // Realtime mode
                        setData([{ id: varId, data: measures }]);
                    }
                    setNoDataAvailable(false);
                } else {
                    toggleDatePickerVisibility();
                    setNoDataAvailable(true);
                }
            } else {
                console.error('Error fetching data:', res);
            }
        } catch (error) {
            // La sesión vencida (401) la maneja el interceptor global
            console.error('Error al cargar las mediciones: ', error);
        } finally {
            setLoaded(true);
        }
    };

    const handleOpenEventsModal = async (dayData) => {
        const dateStr = new Date(dayData.fecha).toLocaleDateString('sv-SE', { timeZone: 'UTC' });
        setSelectedDateLabel(dateStr);
        setIrrigationSummary({
            pulsos: dayData.pulsos,
            duration: dayData.duration,
            firstPulse: dayData.firstPulse,
            lastPulse: dayData.lastPulse
        });
        setLoaded(false);
        try {
            const res = await getIrrigationEvents(sensorId, dayData.fecha);
            if (res.status === 200) {
                setSelectedEvents(res.data);
                setShowEventsModal(true);
            }
        } catch (error) {
            console.error("Error fetching irrigation events", error);
        } finally {
            setLoaded(true);
        }
    };

    // Toggle visibility of the date picker modal
    const toggleDatePickerVisibility = () => {
        setShowDatePicker((prev) => !prev);
    };

    // Series used to compute the average of the currently selected/loaded range.
    // Daily mode with Max/Prom/Min uses the 'Prom' series (already averaged per day by the backend);
    // every other mode (realtime, single-value daily, RHS) has a single series in data[0].
    const averageSourceSeries = useMemo(() => (
        currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId)
            ? data.find(d => d.id === 'Prom')?.data || []
            : data[0]?.data || []
    ), [currentViewMode, varId, data]);

    const rangeAverage = useMemo(() => {
        const values = averageSourceSeries
            .map(d => d.y)
            .filter(y => typeof y === 'number' && !Number.isNaN(y));

        if (values.length === 0) return null;

        const avg = values.reduce((sum, y) => sum + y, 0) / values.length;
        return avg;
    }, [averageSourceSeries]);

    const formatPeriodDate = (date) => {
        const pad = (num) => (num < 10 ? '0' + num : num);
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    };

    const periodLabel = useMemo(() => {
        if (range.from && range.to) {
            return `${formatPeriodDate(new Date(range.from))} – ${formatPeriodDate(new Date(range.to))}`;
        }
        if (averageSourceSeries.length > 0) {
            const firstDate = new Date(averageSourceSeries[0].x);
            const lastDate = new Date(averageSourceSeries[averageSourceSeries.length - 1].x);
            return `${formatPeriodDate(firstDate)} – ${formatPeriodDate(lastDate)}`;
        }
        return '';
    }, [range, averageSourceSeries]);

    return (
        <Container id={'_graph' + varId} className="_graph">
            <Row id="ContainerTitleGraph">
                <h3 id="TitleGraph">{varName} </h3>
                <Col id="ContainerButtonsTitle">
                    <CsvDownloadButton
                        id={'csvButton' + varId}
                        data={prepareCsvData()}
                        filename={varName}
                        headers={getCsvHeaders()}
                        target="_blank"
                        title="Download CSV"
                    >
                        <div className="ButtonCSV" />
                    </CsvDownloadButton>

                    {/* Only show local date picker in realtime mode */}
                    {currentViewMode === 'realtime' && (
                        <Button
                            className="ButtonConfImage"
                            title="Threshold"
                            onClick={() => setShowModalThreshold(true)}
                        />
                    )}
                    <Button
                        className="ButtonFilterImage"
                        title="Datapicker"
                        onClick={toggleDatePickerVisibility}
                    />
                </Col>
            </Row>

            {rangeAverage !== null && (
                <Row id="AverageHighlightRow" className="px-3">
                    <span id="AverageHighlightValue">
                        Promedio de {varName}: <strong>{rangeAverage.toFixed(2)} {units}</strong>
                    </span>
                    {periodLabel && (
                        <span id="AverageHighlightPeriod">Periodo analizado: {periodLabel}</span>
                    )}
                </Row>
            )}

            {currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId) && (
                <Row className="mb-3 px-3">
                    <Form className="d-flex gap-4">
                        {['Max', 'Prom', 'Min'].map((id) => (
                            <Form.Check
                                key={id}
                                type="checkbox"
                                id={`check-${id}`}
                                label={id === 'Prom' ? 'Promedio' : id}
                                checked={visibleSeries.includes(id)}
                                onChange={() => {
                                    setVisibleSeries(prev =>
                                        prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
                                    );
                                }}
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    color: visibleSeries.includes(id) ? '#333' : '#999'
                                }}
                            />
                        ))}
                    </Form>
                </Row>
            )}

            {showDatePicker && (
                <ModalSelectDateMeasure
                    showDatePickerInterGraph={showDatePicker}
                    noDataAvailableInterGraph={noDataAvailable}
                    changeDatePickerVisibility={toggleDatePickerVisibility}
                    range={range}
                    oldRange={oldRange}
                    setOldRange={setOldRange}
                    setRange={setRange}
                    varIdGraph={varId}
                    data={data}
                    setData={setData}
                    requestMeasures={requestMeasures}
                    currentViewMode={currentViewMode}
                />
            )}
            {showModalThreshold && currentViewMode === 'realtime' && (
                <ModalUpdateThreshold
                    showModalThresholdInterGraph={showModalThreshold}
                    initialMaxThresholdGraph={graphData.initialMaxThreshold}
                    actualMaxThresholdGraph={savedThreshold.max}
                    initialMinThresholdGraph={graphData.initialMinThreshold}
                    actualMinThresholdGraph={savedThreshold.min}
                    unitsGraph={graphData.units}
                    varIdGraph={varId}
                    sensorIdGraph={sensorId}
                    setShowModalThreshold={setShowModalThreshold}
                    onThresholdSaved={setSavedThreshold}
                />
            )}

            <Row id="GraphRow">
                {!loaded && <LoadingComponent data={loadingProps} />}
                {loaded && (
                    data.length > 0 && data.some(series => series.data && series.data.length > 0) ? (
                        currentViewMode === 'daily' && varId === 'RHS' ? (
                            <ResponsiveBar
                                data={data[0].data.map(d => ({
                                    fecha: d.x,
                                    pulsos: d.y,
                                    duration: d.duration,
                                    firstPulse: d.firstPulse,
                                    lastPulse: d.lastPulse
                                }))}
                                keys={['pulsos']}
                                indexBy="fecha"
                                margin={{ top: 40, right: 20, bottom: 70, left: 80 }}
                                padding={0.3}
                                valueScale={{ type: 'linear' }}
                                indexScale={{ type: 'band', round: true }}
                                colors={color}
                                borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                                axisTop={null}
                                axisRight={null}
                                axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 30,
                                    legend: 'Fecha',
                                    legendPosition: 'middle',
                                    legendOffset: 60,
                                    format: value => {
                                        const date = new Date(value);
                                        return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' });
                                    }
                                }}
                                axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: units,
                                    legendPosition: 'middle',
                                    legendOffset: -50,
                                    tickValues: Array.from(
                                        { length: Math.ceil(Math.max(...data[0].data.map(d => d.y), 1)) + 1 },
                                        (_, i) => i
                                    )
                                }}
                                labelSkipWidth={12}
                                labelSkipHeight={12}
                                labelTextColor={{ from: 'theme', colors: 'black' }}
                                labelFormat={v => Math.round(v)}
                                animate={true}
                                motionConfig="gentle"
                                tooltip={({ data }) => (
                                    <div style={{ background: 'white', padding: '9px 12px', border: '1px solid #ccc', minWidth: '200px' }}>
                                        <div><strong>Fecha: {new Date(data.fecha).toLocaleDateString('sv-SE', { timeZone: 'UTC' })}</strong></div>
                                        <div style={{ color: color[0] }}>
                                            <strong>Pulsos:</strong> {data.pulsos} <br />
                                            <strong>Dur. Total:</strong> {data.duration} min <br />
                                            {data.firstPulse && (
                                                <><strong>1er Pulso:</strong> {new Date(data.firstPulse).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <br /></>
                                            )}
                                            {data.lastPulse && (
                                                <><strong>Últ. Pulso:</strong> {new Date(data.lastPulse).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                                            )}
                                        </div>
                                    </div>
                                )}
                                onClick={(node) => handleOpenEventsModal(node.data)}
                            />
                        ) : (
                            <ResponsiveLine
                                colors={currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId) ? { datum: 'color' } : color}
                                enableArea={currentViewMode !== 'daily'}
                                areaBaselineValue={0}
                                areaOpacity={0.04}
                                lineWidth={3}
                                margin={{ top: 40, right: 20, bottom: 70, left: 80 }}
                                data={currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId)
                                    ? data.filter(d => visibleSeries.includes(d.id))
                                    : data
                                }
                                animate={false}
                                enablePoints={currentViewMode === 'daily'}
                                curve="monotoneX"
                                xScale={{
                                    type: 'time',
                                    format: currentViewMode === 'daily' ? '%Y-%m-%d' : '%Y-%m-%dT%H:%M:%S.%L%Z',
                                    precision: currentViewMode === 'daily' ? 'day' : 'second',
                                    useUTC: currentViewMode !== 'daily',
                                }}
                                xFormat={currentViewMode === 'daily' ? "time:%Y-%m-%d" : "time:%Y-%m-%d-%H:%M:%S"}
                                yScale={{
                                    type: 'linear',
                                    min: 'auto',
                                    max: 'auto',
                                    reverse: false,
                                }}
                                yFormat=",.2f"
                                axisLeft={{
                                    orient: 'left',
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: units,
                                    legendOffset: -50,
                                    legendPosition: 'middle',
                                }}
                                axisBottom={{
                                    orient: 'bottom',
                                    tickSize: 6,
                                    tickPadding: 2,
                                    tickRotation: 30,
                                    legend: 'Fecha',
                                    legendOffset: 60,
                                    legendPadding: 50,
                                    legendPosition: 'middle',
                                    format: currentViewMode === 'daily' ? '%b %d' : (
                                        data.length > 0 && data[0].data.length > 800
                                            ? '%b %d'
                                            : '%b %d - %H:%M'
                                    ),
                                    tickValues: currentViewMode === 'daily' ? 'every 1 day' : (
                                        data.length > 0 && data[0].data.length > 800
                                            ? 'every 1 day'
                                            : 'every 2 hours'
                                    ),
                                }}
                                enableSlices={'x'}
                                sliceTooltip={({ slice }) => currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId) ? (
                                    <div style={{ background: 'white', padding: '9px 12px', border: '1px solid #ccc' }}>
                                        <div><strong>Fecha: {slice.points[0].data.xFormatted}</strong></div>
                                        {slice.points.map((point) => (
                                            <div key={point.id} style={{ color: point.serieColor }}>
                                                <strong>{point.serieId}:</strong> {point.data.yFormatted} {units}
                                                {point.data.occurredAt && (
                                                    <span style={{ fontSize: '0.9em', marginLeft: '5px' }}>
                                                        ({new Date(point.data.occurredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div>
                                        {slice.points.map((point, index) => (
                                            <div key={index} id="tooltipSlice">
                                                Fecha: {point.data.xFormatted} <br />
                                                Valor: {point.data.yFormatted} {units}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                useMesh={true}
                                legends={currentViewMode === 'daily' && !SINGLE_VALUE_DAILY_VARS.includes(varId) ? [
                                    {
                                        anchor: 'top-right',
                                        direction: 'row',
                                        justify: false,
                                        translateX: 0,
                                        translateY: -30,
                                        itemsSpacing: 0,
                                        itemDirection: 'left-to-right',
                                        itemWidth: 80,
                                        itemHeight: 20,
                                        itemOpacity: 0.75,
                                        symbolSize: 12,
                                        symbolShape: 'circle',
                                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                        effects: [
                                            {
                                                on: 'hover',
                                                style: {
                                                    itemBackground: 'rgba(0, 0, 0, .03)',
                                                    itemOpacity: 1
                                                }
                                            }
                                        ]
                                    }
                                ] : []}
                            />
                        )
                    ) : (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px', width: '100%' }}>
                            <p className="text-muted">No hay datos disponibles para mostrar.</p>
                        </div>
                    )
                )}
            </Row>
            {varId === 'RHS' && (
                <IrrigationEventsModal
                    show={showEventsModal}
                    onHide={() => setShowEventsModal(false)}
                    events={selectedEvents}
                    date={selectedDateLabel}
                    summaryData={irrigationSummary}
                />
            )}
        </Container>
    );
};

export default VariableGraph;